import React, { useEffect } from 'react'
import {
  Box,
  Flex,
  Table,
  Thead,
  Tr,
  Tbody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import dayjs from 'dayjs'
import { useLocation, useNavigate } from 'react-router-dom'
import { LocationParameter } from '../../types'
import PercentageChange from '../../../reports/pages/components/EnergyReportComponents/PercentageChange'
import {
  costIncomeMap,
  getChartColor,
  getLocationsPercentage,
  getReadableValue,
  isTemperatureParam,
  measurementTypeMap,
  parameterColumnMap,
  parameterTitleMap,
} from '../../../../utils/helpers'
import { useQueryParams } from '../../hooks/useQueryParams'
import { PERIODPARAMS } from '../../consts'
import { HeaderText } from '../../../settings/components/utils'
import { renderDeviceRowRecursive } from './utils'
import { useDeviceCurrentValues } from '../../../settings/hooks/useDeviceCurrentValues'

export function LineChart({
  data,
  periodLabel,
}: {
  data: LocationParameter
  periodLabel: string
}) {
  const [expandedDeviceIds, setExpandedDeviceIds] = React.useState<Set<string>>(new Set())
  const [selectedDeviceId, setSelectedDeviceId] = React.useState<string | null>(null)
  const [unit, setUnit] = React.useState<'kWh' | 'лв.'>('kWh')
  const chartColor = getChartColor(data.parameter_title)
  const gradientId = `currentColor-${data.parameter_title}`
  const [queryParams] = useQueryParams(PERIODPARAMS)
  const navigate = useNavigate()
  const {deviceCurrentValues, isConnected}= useDeviceCurrentValues()

  const location = useLocation()

  const chartKey = React.useMemo(() => {
    const slug = (data.parameter_title || 'chart')
      .toLowerCase()
      .replace(/[^a-z0-9]+/gi, '_')
    return `expanded_${slug}`
  }, [data.parameter_title])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const exp = params.get(chartKey)
    if (exp) {
      setExpandedDeviceIds(new Set(exp.split(',').filter(Boolean)))
    }
  }, [location.search, chartKey])

  const chartData = data.periods.map((p) => ({
    label: dayjs(p.period).format(periodLabel === 'day' ? 'D MMM HH:mm' : 'D MMM'),
    yValue: isTemperatureParam(data.parameter_title)
      ? p.value
      : unit === 'kWh'
      ? p.value
      : p.cost,
  }))

  const total = data.total
  const prevTotal = data.prev_total

  const toggleDeviceExpand = (rawId: string | number) => {
    const id = String(rawId)
    setExpandedDeviceIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)

      const params = new URLSearchParams(location.search)
      if (next.size) {
        params.set(chartKey, Array.from(next).join(','))
      } else {
        params.delete(chartKey)
      }

      navigate({ search: params.toString() }, { replace: true })
      return next
    })
  }

  return (
    <Flex width="100%" direction="column" p={4} gap={6}>
      <Flex justify="space-between" align="center">
        <Stat>
          <StatLabel fontWeight={700} fontSize="16px" fontFamily="Inter" color="#484848">
            {getReadableValue(parameterTitleMap, data.parameter_title)}
          </StatLabel>
          <Flex alignItems="center" gap={2}>
            <StatNumber>
              {total?.toFixed(2)}{' '}
              <span
                style={{
                  fontSize: '15px',
                  fontWeight: 400,
                  color: '#9B9B9B',
                }}
              >
                {getReadableValue(measurementTypeMap, data.parameter_title)}
              </span>
            </StatNumber>
            <StatHelpText>
              <PercentageChange
                percentageChange={getLocationsPercentage(total, prevTotal)}
              />
            </StatHelpText>
          </Flex>
        </Stat>

        {!isTemperatureParam(data.parameter_title) && (
          <Flex gap={2} alignItems="center">
            <Flex borderRadius="full" bg="#F5F6F7" padding="2px">
              {['kWh', 'лв.'].map((u) => (
                <Box
                  key={u}
                  px={3}
                  py="0.2rem"
                  borderRadius="full"
                  cursor="pointer"
                  fontSize="13px"
                  fontWeight={unit === u ? 700 : 400}
                  fontFamily="Inter"
                  bg={unit === u ? '#484848' : 'transparent'}
                  color={unit === u ? 'white' : '#484848'}
                  _hover={{
                    bg: unit === u ? 'gray.700' : 'gray.200',
                  }}
                  onClick={() => setUnit(u as 'kWh' | 'лв.')}
                >
                  {u}
                </Box>
              ))}
            </Flex>
          </Flex>
        )}
      </Flex>

      <Box h="300px">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={chartColor} stopOpacity={0.3} />
                <stop offset="95%" stopColor={chartColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{
                fontSize: 12,
                fontWeight: 400,
                color: '#484848',
                fontFamily: 'Inter',
              }}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip
              formatter={(value: number) => [
                `${value?.toFixed(2)} ${
                  unit === 'kWh'
                    ? getReadableValue(measurementTypeMap, data.parameter_title)
                    : 'лв.'
                }`,

                unit === 'kWh'
                  ? isTemperatureParam(data.parameter_title)
                    ? 'Temperature'
                    : 'Energy'
                  : 'Cost',
              ]}
            />
            <Area
              type="monotone"
              dataKey="yValue"
              stroke={chartColor}
              fill={`url(#${gradientId})`}
              strokeWidth={3}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>

      <>
        <Box>
          <Table
            size="sm"
            variant="simple"
            sx={{
              'thead th': { borderBottom: 'none' },
              'tbody td, tbody th': { borderBottom: 'none' },
            }}
          >
            <Thead>
              <Tr>
                <HeaderText>Device</HeaderText>
                <HeaderText textAlign="right">Current</HeaderText>
                <HeaderText isNumeric>
                  {!isTemperatureParam(data.parameter_title) ? 'Total ' : ''}
                  {getReadableValue(parameterColumnMap, data.parameter_title)}
                </HeaderText>
                {!isTemperatureParam(data.parameter_title) && (
                  <HeaderText isNumeric>
                    {getReadableValue(costIncomeMap, data.parameter_title)}
                  </HeaderText>
                )}
              </Tr>
            </Thead>
            <Tbody>
              {data.devices.map((device) =>
                renderDeviceRowRecursive(
                  device,
                  0,
                  queryParams.from,
                  queryParams.to,
                  queryParams.period,
                  expandedDeviceIds,
                  toggleDeviceExpand,
                  selectedDeviceId,
                  setSelectedDeviceId,
                  data.parameter_title,
                  device.parameters?.find(
                    (x) => x.parameter_title.toLowerCase() === data.parameter_title,
                  )?.value ?? 0,
                  device.parameters?.find(
                    (x) => x.parameter_title.toLowerCase() === data.parameter_title,
                  )?.total_cost ?? 0,
                  navigate,
                  device?.is_online,
                  deviceCurrentValues
                )
              )}
            </Tbody>
          </Table>
        </Box>
        <Box my={3} border="5px solid #DEDFE2" opacity="20%" borderRadius="full" />
      </>
    </Flex>
  )
}
